// components/InvoiceDashboard.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiSearch, FiDownload, FiEye, FiX,
  FiFileText, FiDollarSign, FiUsers, FiCalendar,
} from 'react-icons/fi';
import API_CONFIG from '../components/API_CONFIG/API_CONFIG';

const formatPrice = (price) => {
  return new Intl.NumberFormat('fr-CD', {
    style: 'currency',
    currency: 'CDF',
    minimumFractionDigits: 0,
  }).format(price || 0);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const InvoiceDashboard = ({ tel }) => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Récupération des factures
  useEffect(() => {
    const fetchData = async () => {
      if (!tel) {
        setError("Numéro de téléphone manquant");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const baseUrl = API_CONFIG.apiUrl || 'http://localhost/api/';
        const url = `${baseUrl}get_invoices.php?tel=${encodeURIComponent(tel)}`;
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        const textResponse = await response.text();
        if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);
        if (!textResponse.trim()) throw new Error("Réponse du serveur vide");
        let result;
        try {
          result = JSON.parse(textResponse);
        } catch (e) {
          throw new Error("Le serveur n'a pas renvoyé un JSON valide.");
        }
        if (result.success) {
          const formatted = (result.data || []).map(inv => ({
            ...inv,
            total_ht: parseFloat(inv.total_ht) || 0,
            tax_amount: parseFloat(inv.tax_amount) || 0,
            total_ttc: parseFloat(inv.total_ttc) || 0,
            items: (inv.items || []).map(item => ({
              ...item,
              quantity: parseFloat(item.quantity) || 0,
              unit_price: parseFloat(item.unit_price) || 0,
              total: parseFloat(item.total) || 0,
            })),
          }));
          setInvoices(formatted);
          setFilteredInvoices(formatted);
        } else {
          setError(result.message || "Erreur lors du chargement");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [tel]);

  // Filtrage par recherche
  useEffect(() => {
    const filtered = invoices.filter(invoice =>
      (invoice.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (invoice.buyer_phone || '').includes(searchTerm) ||
      (invoice.nif || '').includes(searchTerm) ||
      (invoice.id || '').toString().includes(searchTerm)
    );
    setFilteredInvoices(filtered);
    setCurrentPage(1);
  }, [searchTerm, invoices]);

  // Pagination
  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);
  const currentItems = filteredInvoices.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const exportToCSV = () => {
    if (filteredInvoices.length === 0) return;
    const headers = ['ID', 'Société', 'Total TTC', 'Date'];
    const csvData = filteredInvoices.map(inv => [inv.id, inv.company_name, inv.total_ttc, inv.created_at]);
    const csvContent = [headers.join(','), ...csvData.map(row => row.join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `factures_${tel}.csv`;
    link.click();
  };

  const viewDetails = (invoice) => {
    setSelectedInvoice(invoice);
    setShowModal(true);
  };

  const stats = {
    total: filteredInvoices.length,
    totalTTC: filteredInvoices.reduce((sum, inv) => sum + inv.total_ttc, 0),
    companies: new Set(filteredInvoices.map(inv => inv.company_name)).size,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950/50 to-indigo-950 p-4 md:p-8 relative">
      {/* Fond décoratif */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="relative max-w-7xl mx-auto">
        {/* En-tête avec bouton retour */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6"
        >
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-xl bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10 transition-colors"
              title="Retour"
            >
              <FiArrowLeft size={20} />
            </button>
            <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent flex items-center gap-2">
              <FiFileText className="text-blue-400" /> Mes Factures
            </h1>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <div className="relative flex-1">
              <FiSearch className="absolute left-3 top-3 text-slate-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-blue-400/20 rounded-2xl text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400/20"
              />
            </div>
            <button
              onClick={exportToCSV}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-500/10 border border-blue-400/20 text-blue-400 rounded-xl text-sm font-medium hover:bg-blue-500/20 transition-colors"
            >
              <FiDownload size={16} /> Exporter
            </button>
          </div>
        </motion.div>

        {/* Statistiques */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          {[
            { label: 'Total Factures', value: stats.total, icon: FiFileText, color: 'text-blue-400' },
            { label: 'Total TTC', value: formatPrice(stats.totalTTC), icon: FiDollarSign, color: 'text-emerald-400' },
            { label: 'Sociétés', value: stats.companies, icon: FiUsers, color: 'text-purple-400' },
          ].map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-2xl p-4 md:p-5"
            >
              <s.icon className={`text-2xl mb-2 ${s.color}`} />
              <div className="text-white font-bold text-lg md:text-xl">{s.value}</div>
              <div className="text-slate-400 text-xs">{s.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Liste des factures */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-blue-400/20 border-t-blue-400 rounded-full animate-spin" />
            <p className="text-slate-300 mt-4">Chargement...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <p className="text-red-400 mb-4">⚠️ {error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-blue-500/10 border border-blue-400/20 text-blue-400 rounded-xl hover:bg-blue-500/20"
            >
              🔄 Réessayer
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8">
              <AnimatePresence>
                {currentItems.map((invoice) => (
                  <motion.div
                    key={invoice.id}
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    whileHover={{ y: -5 }}
                    className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-2xl p-5 shadow-lg"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-white font-semibold text-lg truncate">{invoice.company_name || 'N/A'}</h3>
                      <span className="text-xs text-blue-400 bg-blue-500/10 px-2 py-1 rounded-full">
                        #{String(invoice.id).padStart(4, '0')}
                      </span>
                    </div>
                    <div className="space-y-2 text-sm text-slate-300 mb-4">
                      <div className="flex justify-between">
                        <span className="text-slate-400"><FiCalendar className="inline mr-1" />Date</span>
                        <span>{formatDate(invoice.created_at)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">NIF</span>
                        <span>{invoice.nif || 'N/A'}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="bg-white/5 rounded-xl p-2 text-center">
                        <div className="text-xs text-slate-400">HT</div>
                        <div className="text-white font-bold">{formatPrice(invoice.total_ht)}</div>
                      </div>
                      <div className="bg-white/5 rounded-xl p-2 text-center">
                        <div className="text-xs text-slate-400">TTC</div>
                        <div className="text-emerald-400 font-bold">{formatPrice(invoice.total_ttc)}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => viewDetails(invoice)}
                      className="w-full py-2.5 rounded-xl bg-blue-500/10 border border-blue-400/20 text-blue-400 text-sm font-medium flex items-center justify-center gap-1 hover:bg-blue-500/20 transition-colors"
                    >
                      <FiEye size={16} /> Voir détails
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>
              {currentItems.length === 0 && (
                <div className="col-span-full text-center py-10 text-slate-400">
                  Aucune facture trouvée
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 pb-8">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30 hover:bg-white/10"
                >
                  <FiArrowLeft size={16} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 rounded-lg text-sm font-medium ${
                      currentPage === page
                        ? 'bg-blue-500/20 text-blue-400 border border-blue-400/30'
                        : 'text-slate-400 hover:bg-white/10'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30 hover:bg-white/10"
                >
                  <FiArrowLeft className="rotate-180" size={16} />
                </button>
              </div>
            )}
          </>
        )}

        {/* Modal détails */}
        <AnimatePresence>
          {showModal && selectedInvoice && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4"
              onClick={() => setShowModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, y: 50 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 50 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-gradient-to-b from-blue-950/95 to-slate-900/95 backdrop-blur-xl border border-blue-400/20 rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6"
              >
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-white">{selectedInvoice.company_name}</h2>
                  <button
                    onClick={() => setShowModal(false)}
                    className="p-2 rounded-xl bg-white/5 text-slate-400 hover:text-white"
                  >
                    <FiX size={20} />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                  <div><span className="text-slate-500">Adresse</span><p className="text-white">{selectedInvoice.address || 'N/A'}</p></div>
                  <div><span className="text-slate-500">RCCM / NIF</span><p className="text-white">{selectedInvoice.rccm || 'N/A'} / {selectedInvoice.nif || 'N/A'}</p></div>
                </div>

                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-400 border-b border-white/10">
                      <th className="pb-2">Désignation</th>
                      <th className="pb-2">Qté</th>
                      <th className="pb-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoice.items.map((item, idx) => (
                      <tr key={idx} className="border-b border-white/5">
                        <td className="py-2 text-white">{item.description}</td>
                        <td className="py-2 text-white">{item.quantity}</td>
                        <td className="py-2 text-right text-white">{formatPrice(item.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="mt-4 text-right">
                  <div className="text-slate-300">Taxe : {formatPrice(selectedInvoice.tax_amount)}</div>
                  <div className="text-xl font-bold text-emerald-400">
                    TOTAL TTC : {formatPrice(selectedInvoice.total_ttc)}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default InvoiceDashboard;